/**
 * TemplateDocumentView (D2 Item 2) — contracts, confirmation forms and packing
 * standards in the client portal.
 *
 * Rendered from the document's STORED html (documents.rendered_content), not
 * from the template. The client sees exactly what was generated, and a later
 * template edit cannot change a document that is already in front of someone.
 * That is the same guarantee the signed-PDF flow depends on.
 *
 * The html carries its own document stylesheet (the shared shell), so this
 * component supplies the chrome around it and nothing else — no second set of
 * styles that could disagree with the PDF.
 */

import React from 'react';
import { PortalActions } from './PortalActions';
import { formatDate } from '../../lib/utils';
import { Icons } from '../../lib/icons';
import { downloadPortalPdf, DownloadState } from './downloadPdf';
import { formatDocNo } from '../../lib/jobNumber';

export function TemplateDocumentView({ data, pdfPath }: { data: any; pdfPath: string }) {
  const d = data.templateDocument ?? {};
  const job = data.job ?? {};

  const [pdf, setPdf] = React.useState<DownloadState>({ busy: false, error: null });
  const onDownload = async () => {
    setPdf({ busy: true, error: null });
    setPdf({ busy: false, error: await downloadPortalPdf(pdfPath) });
  };

  return (
    <>
      <div className="pp-head">
        <div>
          <div className="pp-kicker">Baltic Van Lines</div>
          <h1 className="pp-h1">{d.label}</h1>
          <div className="pp-jobno">{formatDocNo(d.number)}</div>
        </div>
        <span className={`pp-badge${d.completedAt ? ' ok' : ''}`}>
          {d.completedAt ? 'Completed' : d.status === 'sent' ? 'Awaiting you' : d.status}
        </span>
      </div>

      {/* The generated document, exactly as stored. */}
      <div className="pp-card">
        <div
          className="pp-doc-embed"
          dangerouslySetInnerHTML={{ __html: extractBody(d.html ?? '') }}
        />
      </div>

      {d.generatedAt && (
        <p className="pp-sub pp-noprint">Prepared {formatDate(String(d.generatedAt).slice(0, 10))} for job #{job.number}.</p>
      )}

      <PortalActions>
        <button className="pp-btn" onClick={onDownload} disabled={pdf.busy}>
          <Icons.document size={16} aria-hidden="true" /> {pdf.busy ? 'Preparing…' : 'Download PDF'}
        </button>
      </PortalActions>
      {pdf.error && <div className="pp-pdf-error pp-noprint">{pdf.error}</div>}
    </>
  );
}

/**
 * rendered_content is a FULL html document (doctype, head, the document
 * stylesheet, body). Injecting all of that inside an existing page would put a
 * second <html> in the DOM; browsers tolerate it by discarding the wrapper, but
 * the <style> goes with it and the document renders unstyled.
 *
 * So: lift out the <style> and the body content, and keep both.
 */
function extractBody(html: string): string {
  const style = html.match(/<style[\s\S]*?<\/style>/i)?.[0] ?? '';
  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const body = bodyMatch ? bodyMatch[1] : html;
  return style + body;
}
